<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';

    import { cn } from '$lib/utils/cn';

    import StarIcon from './StarIcon.svelte';
    import { compressAvatar } from '../_utils/compressAvatar';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import { getThemedOrCustomColor } from '../_utils/getThemedOrCustomColor';
    import Box from '../_wrappers/Box.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { Box as BoxType, LeftCenterAlign } from '$lib/types/Components/SharedTypes';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    export let id = '';
    export let box: BoxType;
    export let align: LeftCenterAlign = 'center';
    export let images: string[] = [];
    export let color = '#FACC15';
    export let showStars = true;
    export let showImages = true;
    export let rating = 5;
    export let wysiwygHtml: string | undefined = undefined;
    export let wysiwyg: JSONContent = {
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                attrs: {
                    class: null,
                },
                content: [
                    {
                        type: 'text',
                        marks: [
                            {
                                type: 'textStyle',
                                attrs: {
                                    color: '',
                                    fontSize: '14px',
                                },
                            },
                        ],
                        text: 'from 200+ reviews',
                    },
                ],
            },
        ],
    };
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout = DEFAULT_LAYOUT;
    export let theme: Theme;

    const isInColumn = layout.isInColumn;
</script>

<Box {id} {...getBoxAttributes({ box, isInColumn })} {transitionDelay} {transitionEffect}>
    <div
        class={cn('fun-flex fun-items-center fun-justify-center', {
            'fun-flex-col': align === 'center',
            'fun-justify-start': align === 'left' && isInColumn,
        })}
    >
        {#if showImages}
            <div
                class={cn(
                    'fun-flex fun-flex-shrink-0 fun-items-center fun-justify-center fun--space-x-3',
                    {
                        'fun-mb-2': align === 'center',
                    },
                )}
            >
                {#each images as image, index (`${image}-${index}`)}
                    <div
                        class={cn(
                            'fun-relative fun-overflow-hidden fun-rounded-full fun-border-2',
                            images.length === 1 ? 'fun-size-14' : 'fun-size-10',
                        )}
                        style:border-color={isInColumn
                            ? 'white'
                            : getThemedOrCustomColor(
                                  box?.backgroundColor,
                                  'backgroundColor',
                                  theme,
                              )}
                    >
                        <img
                            src={compressAvatar(image)}
                            alt={`Avatar ${index}`}
                            class="fun-absolute fun-inset-0 fun-size-full fun-object-cover"
                        />
                    </div>
                {/each}
            </div>
        {/if}

        <div class={cn({ 'fun-ml-5': align === 'left' && showImages }, 'fun-space-y-2')}>
            {#if showStars}
                <div
                    class={cn('fun-relative fun-isolate fun-h-6 fun-w-[120px]', {
                        'fun-mx-auto': align === 'center',
                    })}
                >
                    <!-- Empty stars -->
                    <div class="fun-absolute fun-inset-0 fun-flex fun-size-full fun-items-center">
                        <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
                        {#each [...Array(5)] as _, index (`star-${index}`)}
                            <StarIcon />
                        {/each}
                    </div>
                    <!-- Filled stars -->
                    <div
                        class="fun-absolute fun-inset-0 fun-z-10 fun-flex fun-h-full fun-items-center fun-overflow-hidden"
                        style:width={`${(rating / 5) * 100}%`}
                    >
                        <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
                        {#each [...Array(5)] as _, index (`star-filled-${index}`)}
                            <StarIcon filled {color} />
                        {/each}
                    </div>
                </div>
            {/if}
            <div
                class={cn('fun-text-left fun-text-sm [&_span]:[overflow-wrap:anywhere]', {
                    'fun-text-center': align === 'center',
                })}
            >
                <Text
                    raw={wysiwyg}
                    {wysiwygHtml}
                    {align}
                    color={theme?.fontColor}
                    box={DEFAULT_BOX}
                    transitionEffect="none"
                />
            </div>
        </div>
    </div>
</Box>
